import { useQuery } from "@apollo/client";
import _ from "lodash";
import { DateTime } from "luxon";
import { ReactElement, useMemo } from "react";
import { StringParam, useQueryParams, withDefault } from "use-query-params";

import {
  IntegrationServiceType,
  ListTenantIntegrationsQuery,
  ListTenantIntegrationsQueryVariables,
  UsageOfTenantQuery,
  UsageOfTenantQueryVariables,
} from "~frontend/__generated__/graphql";
import ErrorCard from "~frontend/components/ErrorCard";
import { FlexBox } from "~frontend/components/FlexBox";
import Loading from "~frontend/components/Loading";
import PageContainer from "~frontend/components/PageContainer";
import { PageHeader } from "~frontend/components/PageHeader";
import UsageBreakdownTable from "~frontend/components/usage/UsageBreakdownTable";
import UsageDateRangePicker from "~frontend/components/usage/UsageDateRangePicker";
import UsageTimeline from "~frontend/components/usage/UsageTimeline";
import { LIST_TENANT_INTEGRATIONS } from "~frontend/graphql/integration";
import { USAGE_OF_TENANT } from "~frontend/graphql/usage";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { useTenant } from "~frontend/providers/LoggedInUserProvider";

function UsageData({
  startDate,
  endDate,
}: {
  startDate: DateTime;
  endDate: DateTime;
}) {
  useBreadcrumbs([
    {
      label: "Organization Usage",
      path: "/organization/usage",
    },
  ]);

  const tenant = useTenant()!;
  const { loading, error, data } = useQuery<
    UsageOfTenantQuery,
    UsageOfTenantQueryVariables
  >(USAGE_OF_TENANT, {
    variables: {
      startDate: startDate.toISODate()!,
      endDate: endDate.toISODate()!,
    },
  });

  const listIntegrationsResult = useQuery<
    ListTenantIntegrationsQuery,
    ListTenantIntegrationsQueryVariables
  >(LIST_TENANT_INTEGRATIONS, {
    variables: {
      input: {
        service: IntegrationServiceType.ActBlueWebhook,
      },
    },
  });

  const hasActBlueIntegration = useMemo(() => {
    return (
      (listIntegrationsResult.data?.listTenantIntegrations || []).length > 0
    );
  }, [listIntegrationsResult]);

  if (loading) {
    return <Loading />;
  }

  if (error || listIntegrationsResult.error) {
    return <ErrorCard errors={[error]} />;
  }

  const donationTotals = {
    donationCount: _.sumBy(data!.usageOfTenantBySubaccount, "donationCount"),
    recurringDonationCount: _.sumBy(
      data!.usageOfTenantBySubaccount,
      "recurringDonationCount"
    ),
    amountRaisedInCents: _.sumBy(
      data!.usageOfTenantBySubaccount,
      "amountRaisedInCents"
    ),
  };

  return (
    <>
      <UsageTimeline
        startDate={startDate}
        endDate={endDate}
        data={data!.usageOfTenantByDay}
        donationTotals={donationTotals}
        showFundraisingFields={hasActBlueIntegration}
      />
      <PageHeader>Subaccounts</PageHeader>

      <UsageBreakdownTable
        nameTitle="Subaccount"
        rows={data!.usageOfTenantBySubaccount.map(row => ({
          smsSegments: row.smsSegments,
          smsMessages: row.smsMessages,
          mmsMessages: row.mmsMessages,
          smsUnregisteredSegments: row.smsUnregisteredSegments,
          smsUnregisteredMessages: row.smsUnregisteredMessages,
          mmsUnregisteredMessages: row.mmsUnregisteredMessages,
          smsShortcodeSegments: row.smsShortcodeSegments,
          smsShortcodeMessages: row.smsShortcodeMessages,
          mmsShortcodeMessages: row.mmsShortcodeMessages,
          donationCount: row.donationCount,
          recurringDonationCount: row.recurringDonationCount,
          amountRaisedInCents: row.amountRaisedInCents,
          id: row.subaccount.id,
          name: row.subaccount.name,
          link: `/subaccounts/${row.subaccount.slug}/usage`,
          hasActBlueIntegration:
            row.subaccount.integrations.filter(
              integration =>
                integration.service === IntegrationServiceType.ActBlueWebhook
            ).length > 0,
        }))}
        searchTitle="Find a subaccount..."
        csvExportFileName={`${
          tenant.name
        }_usage_by_subaccount_${startDate.toISODate()}_${endDate.toISODate()}.csv`}
      />
    </>
  );
}

function validateStartDate(startDate: string): DateTime {
  const dt = DateTime.fromISO(startDate);
  if (!dt.isValid) {
    return DateTime.now().startOf("month");
  }
  return dt;
}

function validateEndDate(
  endDate: string,
  validatedStartDate: DateTime
): DateTime {
  const dt = DateTime.fromISO(endDate);
  if (!dt.isValid || dt < validatedStartDate) {
    return DateTime.now().endOf("month");
  }
  return dt;
}

export default function SubaccountUsage(): ReactElement {
  const [dateRange, setDateRange] = useQueryParams({
    start_date: withDefault(
      StringParam,
      DateTime.now().startOf("month").toISODate()
    ),
    end_date: withDefault(
      StringParam,
      DateTime.now().endOf("month").toISODate()
    ),
  });

  const validatedStartDate = validateStartDate(dateRange.start_date as string);
  const validatedEndDate = validateEndDate(
    dateRange.end_date as string,
    validatedStartDate
  );

  return (
    <PageContainer>
      <FlexBox justifyContent="space-between">
        <PageHeader>Organization Usage</PageHeader>
        <UsageDateRangePicker
          value={{ startDate: validatedStartDate, endDate: validatedEndDate }}
          onChange={({ startDate, endDate }) => {
            setDateRange({
              start_date: startDate.toISODate()!,
              end_date: endDate.toISODate()!,
            });
          }}
        />
      </FlexBox>
      <UsageData startDate={validatedStartDate} endDate={validatedEndDate} />
    </PageContainer>
  );
}
